import React, {
  ChangeEvent,
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState
} from 'react'
import { Col, Row } from 'react-grid-system'
import { Controller, useForm, FieldErrors } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import {
  AccountDetailsHeaderContainer,
  FieldWrapper,
  StyledAccountDetailsAvatar,
  StyledAccountDetailsBar,
  StyledAccountDetailsHeaderTitle,
  StyledAccountDetailsSummary,
  StyledAccountDetailsSummaryContainer,
  StyledAccountDetailsSummaryItem,
  StyledUsers,
  StyledUsersBar,
  StyledCountBadge,
  StyledClinPrimaryButton,
  StyledClinSecondaryButton,
  StyledPhoneExtension,
  StyledPhoneExtensionNumber,
  StyledPrimaryBadge,
  StyledRow,
  StyledRowText,
  StyledRowTitle,
  StyledShippingAddress,
  StyledShippingAddressBar,
  StyledShippingAddressItem,
  StyledShippingAddressSubBar,
  StyledUserName,
  StyledUsersShippingContainer,
  StyledLoadingContainer,
  StyledClinSecondaryButton2,
  StyledAccountDetailsAddress
} from './AccountDetails.styles'
import { EditableFields } from './AccountDetailsCountainer'
import { ClinTheme } from '../../../ClinTheme'
import { ICrumb } from '../../../components/ClinBreadcrumb'
import { ClinButton } from '../../../components/ClinButton'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinSelect, ClinSelectVariant } from '../../../components/ClinSelect'
import { ClinSpacer } from '../../../components/ClinSpacer'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { ClinTextInput } from '../../../components/ClinTextInput'
import {
  countryCodes,
  PhoneContactResponseType,
  PhoneResponseType,
  phoneTypesRecord,
  UserRole
} from '../../../constants'
import { IContact, TPhoneResponseType } from '../../../types/IContact'
import {
  ContactCardDto,
  CountryDto,
  CreateShippingAddressDto,
  InstituteContactDto,
  InstituteDto,
  LovDto,
  OrgAddressDto,
  UpdateContactDto,
  UserPhoneDto
} from '../../../types/swaggerTypes'

import '../../../i18n/config'

import { getSquidExObjectForKeyAndValue } from '../../../utils/getSquidExObjectForKeyAndValue'
import { IRole } from '../../../types'
import {
  getInputPlaceholder,
  PlaceholderType
} from '../../../utils/Forms/getInputPlaceholder'
import {
  getInputValidation,
  ValidationType
} from '../../../utils/Forms/getInputValidation'
import { StyledHeaderClinPageContentFrame } from '../../OrderPages/OrderDetail/OrderDetail.styles'
import { StyledLoadingSpinner } from '../../Onboarding/Onboarding.styles'
import { ClinSpinner } from '../../../components/ClinSpinner'
import { ClinGroup } from '../../../components/ClinGroup'
import { ClinText } from '../../../components/ClinText'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import { IPagination } from '../../../components/ClinPagination/ClinPagination.model'
import {
  AnnounceMode,
  ClinAnnounceBar
} from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import { AnalyticsEvent } from '../../../services/Analytics'
import { AccountDetailsUserCard } from './AccountDetailsUserCard'
import { AccountDetailsAddressBlock } from './AccountDetailsAddressBlock'
import { useHistory } from 'react-router'
import { StyledSpinnerContainer } from '../../../components/ClinSpinner/ClinSpinner.styles'
import {
  AccountDetailsChangePassword,
  FormSubmitState
} from './AccountDetailsChangePassword'
import { AccountDetailsAddressForm } from './AccountDetailsAddressForm'
import { formatValueLabel } from '../../../utils/formatValueLabel'

interface IAccountDetailsProps {
  /** Whether the submission API request is in progress */
  isSubmitting?: boolean
  /** Which field is in edit mode */
  fieldToEdit?: EditableFields | string
  /** Users current institute */
  institute: InstituteDto
  /** Additional user details to be provided */
  userDetails: ContactCardDto
  /** An array of activeContacts for an institute **/
  activeContacts: IContact[]
  /** An array of pendingContacts for an institute **/
  pendingContacts: IContact[]
  /** Pagination details */
  pagination: IPagination
  /** An array of specialism values */
  specialisms: LovDto[]
  /** An array of job type values */
  jobTitles: LovDto[]
  /** Breadcrumbs to display */
  crumbs?: ICrumb[]
  /** Back button text (different for aus ga users) */
  backButtonText?: string
  /** State of the form */
  passwordFormState?: FormSubmitState
  /** When change button clicked */
  handleFormSubmission: (data: UpdateContactDto) => void
  /** When change institute button is clicked */
  handleChangeInstitute(): void
  /** When requesting to edit a field */
  handleEditRequest: (field: EditableFields | string) => void
  /** When requesting to go back to the dashboard */
  handleRequestToExitEditing(): void
  /** When requesting to go back to the dashboard */
  handleRequestToGoBack(): void
  /** New password callback when form submitted */
  handleChangePasswordSubmit(p: string, newP: string, newPConfirm: string): void
  /** Handle click on "Log out" button */
  handleLogOut: () => void
  /** An event for when a request is made for a new user */
  onRequestNewUser: () => void
  /** An event for when a request is made to edit a user */
  onEditUser: (userKey: string) => void
  /** An event for when a request is made to remove a user */
  onRemoveUser: (user: IContact, isInvite: boolean) => void
  /** Called when the user clicks a page on the pagination */
  onPageClicked: (selectedPageIndex: number) => void
  /** Called when the user selects a different page size in the pagination */
  onPageSizeChange: (pageSize: number) => void
  /** An event for when a request is made to create a shipping address */
  onRequestNewShippingAddress: () => void
  /** An event for when a request is made to remove a shipping address */
  onRemoveAddress: (address: OrgAddressDto) => void
  /** An event for when a request is made to invite a contact */
  onRequestInvite: (contact: IContact) => void
  /** User role */
  isAdmin: boolean
  /** Support contact details **/
  supportContact: CountryDto
  /** Addresses **/
  addressesShipTo: OrgAddressDto[]
  /** Whether the contacts data is currently being loaded */
  isLoadingContacts: boolean
  editingKey: string | null
  handleUserFormSubmission: (
    data: UpdateContactDto,
    contact: InstituteContactDto
  ) => void
  handleUserFormCancel: () => void
  isUserSubmitting: boolean
  handleAddressSubmission: (data: CreateShippingAddressDto) => void
  isSubmittingAddress: boolean
  resetOnSubmit: boolean
  countries: CountryDto[]
  selectedCountry?: string
  addressErrorMessage?: string
  isRequestNewAddress?: boolean
  setIsRequestNewAddress?: (isNewAddress: boolean) => void
  isSpecialismsLoading: boolean
  isJobTitlesLoading: boolean
}

interface FormErrors {
  preferredName?: string
  specialism?: string
  classification?: string
  licenseNumber?: string
  customerPhones?: {
    [index: number]: {
      countryCode?: string
      areaCode?: string
      phoneNumber?: string
      phoneExtension?: string
    }
  }
}

const AccountDetailsSchema = yup.object().shape({
  licenseNumber: yup
    .string()
    .when(
      'classification',
      (classification: string, schema: yup.StringSchema) => {
        const c = (classification || '').toLowerCase()
        const mustHave = c === 'pharmacist' || c === 'physician'
        return mustHave
          ? schema.required(getInputValidation(ValidationType.RequiredField))
          : schema.notRequired()
      }
    ),
  customerPhones: yup.array().of(
    yup.object().shape({
      phoneNumber: yup.string().notRequired()
    })
  )
  // Add other field validations as needed
})

export const AccountDetails: FunctionComponent<IAccountDetailsProps> = ({
  isSubmitting,
  fieldToEdit,
  institute,
  userDetails,
  activeContacts,
  pendingContacts,
  specialisms,
  jobTitles,
  passwordFormState,
  handleFormSubmission,
  handleChangeInstitute,
  handleEditRequest,
  handleRequestToExitEditing,
  handleChangePasswordSubmit,
  handleLogOut,
  onRequestNewUser,
  onEditUser,
  onRemoveUser,
  onRequestInvite,
  onRequestNewShippingAddress,
  onRemoveAddress,
  isAdmin,
  supportContact,
  addressesShipTo,
  isLoadingContacts,
  editingKey,
  handleUserFormSubmission,
  handleUserFormCancel,
  isUserSubmitting,
  countries,
  isSubmittingAddress,
  selectedCountry,
  addressErrorMessage,
  resetOnSubmit,
  handleAddressSubmission,
  isRequestNewAddress,
  setIsRequestNewAddress,
  isSpecialismsLoading,
  isJobTitlesLoading
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [formErrors] = useState<FormErrors>({})
  const [isDisabledLicenceNumber, setIsDisabledLicenceNumber] =
    useState<boolean>()
  const [isMobile, setIsMobile] = React.useState<boolean>(false)

  const skipResetForTarget = React.useRef<EditableFields | null>(null)

  // Form setup
  const formMethods = useForm<ContactCardDto>({
    defaultValues: userDetails,
    resolver: yupResolver(AccountDetailsSchema)
  })

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = formMethods

  // Extract watched values
  const watchClassification = watch('classification')
  const watchCustomerPhones = watch('customerPhones')

  const hasAtLeastOnePhone = useMemo(() => {
    if (!watchCustomerPhones || !Array.isArray(watchCustomerPhones)) {
      return false
    }
    const validPhones = watchCustomerPhones.filter((phone) => {
      return phone?.phoneNumber?.trim()
    })
    return validPhones.length > 0
  }, [watchCustomerPhones])

  const userInitiallyHadPhones = useMemo(() => {
    return (
      userDetails.customerPhones &&
      userDetails.customerPhones.length > 0 &&
      userDetails.customerPhones.some((phone) => phone?.phoneNumber?.trim())
    )
  }, [userDetails.customerPhones])

  useEffect(() => {
    if (userInitiallyHadPhones && !hasAtLeastOnePhone) {
      setError('customerPhones', {
        type: 'manual',
        message: 'At least one phone number is required for this user'
      })
    } else {
      clearErrors('customerPhones')
    }
  }, [hasAtLeastOnePhone, userInitiallyHadPhones, setError, clearErrors])

  // Helper function to prepare phone data for submission
  const preparePhoneData = (phone: any) => ({
    ...phone,
    phoneId: phone?.phoneId?.toString()
  })

  // Helper function to prepare form data for submission
  const prepareFormData = (values: ContactCardDto): UpdateContactDto => ({
    ...values,
    phones: values.customerPhones.map(preparePhoneData)
  })

  const onSubmit = (values: ContactCardDto) => {
    const updateValues = prepareFormData(values)
    handleFormSubmission(updateValues)
  }

  const getPhoneError = (
    index: number,
    field: 'countryCode' | 'areaCode' | 'phoneNumber' | 'phoneExtension'
  ) => {
    return formErrors.customerPhones?.[index]?.[field]
  }

  //Helper function for ui
  const initials = userDetails.name
    .split(' ')
    .map((word) => word[0])
    .join('')

  const updateIsMobile = () => {
    setIsMobile(
      (window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth) <= ClinTheme.breakpoints[2]
    )
  }

  // Get user role
  const userRole = userDetails.clientRelationships.find(
    (item) => item.instituteId === institute.instituteId
  )

  const role = getSquidExObjectForKeyAndValue(
    'key',
    userRole?.roleType as UserRole,
    t('user_roles:roles', {
      returnObjects: true
    })
  ) as IRole

  // Effects
  useEffect(() => {
    handleClassificationChange()
  }, [watchClassification])

  useEffect(() => {
    reset(userDetails)
  }, [reset, userDetails])

  useEffect(() => {
    if (errors.licenseNumber) {
      skipResetForTarget.current = EditableFields.licenseNumber
      handleEditClick(EditableFields.licenseNumber)
      return
    }
  }, [errors])

  useEffect(() => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    return () => {
      window.removeEventListener('resize', updateIsMobile)
    }
  }, [])

  // Extracted functions to reduce complexity
  const handleClassificationChange = () => {
    if (watchClassification) {
      const classification = watchClassification.toLowerCase().toString()
      if (classification === 'physician' || classification === 'pharmacist') {
        clearErrors('licenseNumber')
        setIsDisabledLicenceNumber(false)
      } else {
        setIsDisabledLicenceNumber(true)
      }
    }
  }

  const getRowClassName = (targetField?: EditableFields) => {
    return fieldToEdit === targetField ? 'editing' : 'not-editing'
  }

  const getPhoneNumberField = (item: UserPhoneDto) => {
    if (item.phoneType === PhoneResponseType.MOBILE) {
      return EditableFields.mobileNumber
    } else if (item.phoneType === PhoneResponseType.FAX) {
      return EditableFields.faxNumber
    } else {
      return EditableFields.phoneNumber
    }
  }

  const getPhoneNumberKey = (item: UserPhoneDto) => {
    if (item.phoneType === PhoneResponseType.MOBILE) {
      return 'common:labels.mobile_number'
    } else if (item.phoneType === PhoneResponseType.FAX) {
      return 'common:labels.fax_number'
    } else {
      return 'common:labels.phone_number_one'
    }
  }

  const handleCancelClick = () => {
    if (fieldToEdit === 'Phone Number') {
      setValue('customerPhones.0.countryCode', '')
    }
    handleRequestToExitEditing()
    reset(userDetails)
  }

  const handleEditClick = (targetField: EditableFields | string) => {
    const shouldSkipReset = skipResetForTarget.current === targetField

    if (fieldToEdit && fieldToEdit !== targetField) {
      if (!shouldSkipReset) {
        handleCancelClick()
      }
    }

    skipResetForTarget.current = null
    handleEditRequest(targetField)
  }

  const additionalAddresses = addressesShipTo.filter(
    (address) => address.primaryFlag === 'N'
  )

  const shouldDisableSaveButton = (
    currentField: EditableFields | string,
    errors: FieldErrors<ContactCardDto>,
    shouldDisable?: boolean
  ): boolean => {
    const isPhoneField =
      currentField === EditableFields.phoneNumber ||
      currentField === EditableFields.mobileNumber ||
      currentField === EditableFields.faxNumber
    if (shouldDisable) {
      return shouldDisable
    }
    if (isPhoneField) {
      if (errors.customerPhones && !Array.isArray(errors.customerPhones)) {
        return true
      }

      const phoneErrors = errors.customerPhones
      if (Array.isArray(phoneErrors)) {
        const hasPhoneErrors = phoneErrors.some(
          (phoneError) => phoneError?.phoneNumber
        )
        if (hasPhoneErrors) return true
      }

      const shouldDisable = userInitiallyHadPhones && !hasAtLeastOnePhone
      if (shouldDisable) {
        return true
      }
    }

    return false
  }

  const handlePhoneChange = (value: string, index: number) => {
    const path = `customerPhones.${index}.phoneNumber` as const

    const isRemovingLastPhone =
      userInitiallyHadPhones &&
      value === '' &&
      watchCustomerPhones.filter(
        (p, i) => i !== index && p?.phoneNumber?.trim()
      ).length === 0

    if (isRemovingLastPhone) {
      setError('customerPhones', {
        type: 'manual',
        message: 'At least one phone number is required for this user'
      })
    } else if (value && !/^\d+$/.test(value)) {
      setError(path, {
        type: 'manual',
        message: t('account_details:form.only_digits_allowed')
      })
    } else {
      clearErrors(path)
      const hasEnoughPhones = watchCustomerPhones.some((p, i) =>
        (i === index ? value : p?.phoneNumber)?.trim()
      )

      if (hasEnoughPhones) {
        clearErrors('customerPhones')
      }
    }
  }

  const handleAreaCodeChange = (value: string, index: number) => {
    const path = `customerPhones.${index}.areaCode` as const

    if (value && !/^\d+$/.test(value)) {
      setError(path, {
        type: 'manual',
        message: t('account_details:form.only_digits_allowed')
      })
    } else {
      clearErrors(path)
    }
  }

  const handleExtensionChange = (value: string, index: number) => {
    const path = `customerPhones.${index}.phoneExtension` as const

    if (value && !/^\d+$/.test(value)) {
      setError(path, {
        type: 'manual',
        message: t('account_details:form.only_digits_allowed')
      })
    } else {
      clearErrors(path)
    }
  }

  // Render helper functions
  const renderHiddenFields = () => (
    <div style={{ display: 'none' }}>
      <ClinTextInput
        width="100%"
        {...register(`roles.0.roleName`)}
        id="role-name"
        value={userRole?.roleType}
      />
    </div>
  )

  const renderHeader = () => (
    <AccountDetailsHeaderContainer>
      <StyledHeaderClinPageContentFrame>
        <ClinGroup alignItems={'center'} space={ClinTheme.space[2]}>
          {isMobile && (
            <ClinButton variant="linkButton" onClick={() => history.goBack()}>
              <ClinIcon
                viewBox="0 0 24 24"
                style={{ padding: '12px' }}
                iconName={ClinIconPathName.ArrowLeft}
                iconFill={ClinTheme.colors.primaryLight}
              />
            </ClinButton>
          )}
          <StyledAccountDetailsHeaderTitle
            marginTop={ClinTheme.space[1]}
            marginBottom={ClinTheme.space[1]}
            variant={TypographyVariant.H3}
          >
            {t('account_details:title')}
          </StyledAccountDetailsHeaderTitle>
        </ClinGroup>
      </StyledHeaderClinPageContentFrame>
    </AccountDetailsHeaderContainer>
  )

  const renderEditButton = (targetField: EditableFields | string) =>
    fieldToEdit !== targetField && (
      <Col width="auto" style={{ textAlign: 'right', paddingRight: 40 }}>
        <ClinButton
          variant="linkButton"
          fontSize={ClinTheme.fontSizes[2]}
          style={{ height: 38 }}
          onClick={() => handleEditClick(targetField)}
        >
          {t('common:labels.edit')}
        </ClinButton>
      </Col>
    )

  const renderActionButtons = (
    targetField: EditableFields | string,
    isDisabled?: boolean
  ) => {
    return (
      fieldToEdit === targetField && (
        <Row style={{ marginTop: 16 }}>
          <Col width="100%">
            <ClinGroup>
              <StyledClinSecondaryButton onClick={handleCancelClick}>
                {t('common:labels.cancel')}
              </StyledClinSecondaryButton>
              <StyledLoadingContainer>
                <StyledClinPrimaryButton
                  disabled={
                    isSubmitting ||
                    isDisabled ||
                    shouldDisableSaveButton(fieldToEdit, errors)
                  }
                  type="submit"
                  variant="primary"
                >
                  {t('common:labels.save')}
                </StyledClinPrimaryButton>
                {isSubmitting && (
                  <StyledLoadingSpinner>
                    <ClinSpinner />
                  </StyledLoadingSpinner>
                )}
              </StyledLoadingContainer>
            </ClinGroup>
          </Col>
        </Row>
      )
    )
  }

  return (
    <ClinPageContentFrame isFullWidth>
      {renderHeader()}
      <ClinPageContentFrame>
        <form onSubmit={handleSubmit(onSubmit)}>
          {renderHiddenFields()}
          <StyledAccountDetailsSummary>
            <StyledAccountDetailsSummaryContainer>
              <StyledAccountDetailsSummaryItem>
                <Row nogutter={true}>
                  <Col width="auto">
                    <StyledAccountDetailsAvatar>
                      {initials}
                    </StyledAccountDetailsAvatar>
                  </Col>
                  {isMobile && (
                    <Col>
                      <Row nogutter={true} justify="between">
                        <Col width="auto">
                          <ClinGroup>
                            <ClinText
                              fontWeight={ClinTheme.fontWeights.medium}
                              lineHeight="18px"
                            >
                              {userDetails.name}
                            </ClinText>
                            <StyledUserName>
                              {userDetails.preferredName}
                            </StyledUserName>
                          </ClinGroup>
                          <ClinText wordBreak="break-word" lineHeight="20px">
                            {userDetails.email}
                          </ClinText>
                        </Col>
                        <Col width="auto">
                          <ClinButton
                            onClick={() => handleLogOut()}
                            variant="linkButton"
                            style={{
                              width: 'fit-content',
                              justifyContent: 'end',
                              marginLeft: '8px',
                              height: '40px'
                            }}
                          >
                            {t('navigation:log_out')}
                          </ClinButton>
                        </Col>
                      </Row>
                    </Col>
                  )}
                </Row>
              </StyledAccountDetailsSummaryItem>

              <StyledAccountDetailsSummaryItem
                paddingRight
                paddingLeft
                borderRight
              >
                <ClinText
                  fontWeight={ClinTheme.fontWeights.bold}
                  lineHeight="20px"
                  marginBottom={ClinTheme.space[3]}
                >
                  {t('account_details:personal_information')}
                </ClinText>
                <StyledRow className="not-editing">
                  <Row>
                    <Col>
                      <StyledRowTitle>
                        {t('common:labels.full_name')}
                      </StyledRowTitle>
                      <ClinTextInput
                        width="100%"
                        name="name"
                        id="name"
                        readOnly={true}
                        className={fieldToEdit === undefined ? 'inactive' : ''}
                        value={userDetails.name}
                      />
                    </Col>
                  </Row>
                </StyledRow>
                <StyledRow className="not-editing">
                  <Row>
                    <Col>
                      <StyledRowTitle>
                        {t('common:labels.email')}
                      </StyledRowTitle>
                      <ClinTextInput
                        id="email"
                        width="100%"
                        name="email"
                        readOnly={true}
                        className={fieldToEdit === undefined ? 'inactive' : ''}
                        value={userDetails.email}
                      />
                    </Col>
                  </Row>
                </StyledRow>
                <StyledRow
                  className={getRowClassName(EditableFields.preferredName)}
                >
                  <FieldWrapper
                    isEdit={fieldToEdit === EditableFields.preferredName}
                  >
                    <Row>
                      <Col>
                        <StyledRowTitle
                          isEdit={fieldToEdit === EditableFields.preferredName}
                        >
                          {t('common:labels.preferred_name')}
                        </StyledRowTitle>
                        <ClinTextInput
                          width="100%"
                          {...register('preferredName')}
                          id="preferred-name"
                          className={
                            fieldToEdit === undefined ? 'inactive' : ''
                          }
                          readOnly={
                            !(fieldToEdit === EditableFields.preferredName)
                          }
                        />
                        {formErrors.preferredName && (
                          <span>{formErrors.preferredName}</span>
                        )}
                      </Col>
                      {renderEditButton(EditableFields.preferredName)}
                    </Row>
                    {renderActionButtons(EditableFields.preferredName)}
                  </FieldWrapper>
                </StyledRow>
                <StyledRow className={getRowClassName(EditableFields.password)}>
                  {fieldToEdit === EditableFields.password ? (
                    <FieldWrapper
                      isEdit={fieldToEdit === EditableFields.password}
                    >
                      <AccountDetailsChangePassword
                        formState={passwordFormState}
                        handleSubmitClick={handleChangePasswordSubmit}
                        handleCancelClick={handleCancelClick}
                      />
                    </FieldWrapper>
                  ) : (
                    <Row style={{ flexFlow: 'row' }}>
                      <Col>
                        <StyledRowTitle>
                          {t('common:labels.password')}
                        </StyledRowTitle>
                        <ClinTextInput
                          width="100%"
                          id="password"
                          name="password"
                          readOnly
                          className={
                            fieldToEdit === undefined ? 'inactive' : ''
                          }
                          value="*******"
                        />
                      </Col>
                      {renderEditButton(EditableFields.password)}
                    </Row>
                  )}
                </StyledRow>
              </StyledAccountDetailsSummaryItem>

              <StyledAccountDetailsSummaryItem
                paddingRight
                paddingLeft
                borderRight
              >
                <ClinText
                  fontWeight={ClinTheme.fontWeights.bold}
                  lineHeight="20px"
                  marginBottom={ClinTheme.space[3]}
                >
                  {t('account_details:job_titles')}
                </ClinText>
                <StyledRow
                  className={getRowClassName(EditableFields.specialism)}
                >
                  <FieldWrapper
                    isEdit={fieldToEdit === EditableFields.specialism}
                  >
                    <Row>
                      <Col>
                        <StyledRowTitle
                          isEdit={fieldToEdit === EditableFields.specialism}
                        >
                          {t('common:labels.specialism')}
                        </StyledRowTitle>
                        {fieldToEdit === EditableFields.specialism ? (
                          <Controller
                            name="specialism"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <ClinSelect
                                id="specialism"
                                width="100%"
                                value={value}
                                variant={ClinSelectVariant.primaryV2}
                                onChange={(
                                  changeValue: ChangeEvent<HTMLSelectElement>
                                ) => onChange(changeValue.currentTarget.value)}
                                hasError={!!formErrors.specialism}
                                prompt={formErrors.specialism}
                              >
                                <option value="" disabled={true}>
                                  {getInputPlaceholder(
                                    PlaceholderType.SelectInput
                                  )}
                                </option>
                                {specialisms
                                  .sort((a, b) =>
                                    a.lovValue && b.lovValue
                                      ? a.lovValue.localeCompare(b.lovValue)
                                      : 0
                                  )
                                  .map((specialism: LovDto) => {
                                    return (
                                      <option
                                        key={`specialism-${specialism.generatedId}`}
                                        value={specialism.lovCode}
                                      >
                                        {specialism.lovValue}
                                      </option>
                                    )
                                  })}
                              </ClinSelect>
                            )}
                          />
                        ) : (
                          <ClinTextInput
                            id="specialism"
                            className="inactive"
                            width="100%"
                            name="specialism"
                            readOnly={true}
                            value={formatValueLabel(
                              userDetails.specialism,
                              specialisms
                            )}
                          />
                        )}
                      </Col>
                      {renderEditButton(EditableFields.specialism)}
                    </Row>
                    {renderActionButtons(EditableFields.specialism)}
                  </FieldWrapper>
                </StyledRow>
                <StyledRow
                  className={getRowClassName(EditableFields.classification)}
                >
                  <FieldWrapper
                    isEdit={fieldToEdit === EditableFields.classification}
                  >
                    <Row>
                      <Col>
                        <StyledRowTitle
                          isEdit={fieldToEdit === EditableFields.classification}
                        >
                          {t('common:labels.job_type')}
                        </StyledRowTitle>
                        {fieldToEdit === EditableFields.classification ? (
                          <Controller
                            name="classification"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <ClinSelect
                                id="classification"
                                width="100%"
                                value={value}
                                variant={ClinSelectVariant.primaryV2}
                                onChange={(
                                  changeValue: ChangeEvent<HTMLSelectElement>
                                ) => onChange(changeValue.currentTarget.value)}
                                hasError={!!formErrors.classification}
                                prompt={formErrors.classification}
                              >
                                <option value="" disabled={true}>
                                  {getInputPlaceholder(
                                    PlaceholderType.SelectInput
                                  )}
                                </option>
                                {jobTitles.map((jobType: LovDto) => {
                                  return (
                                    <option
                                      key={`classification-${jobType.generatedId}`}
                                      value={jobType.lovCode}
                                    >
                                      {jobType.lovValue}
                                    </option>
                                  )
                                })}
                              </ClinSelect>
                            )}
                          />
                        ) : (
                          <ClinTextInput
                            id="classification"
                            name="classification"
                            width="100%"
                            readOnly={true}
                            className="inactive"
                            value={formatValueLabel(
                              (watchClassification ||
                                userDetails.classification) as string,
                              jobTitles
                            )}
                          />
                        )}
                      </Col>
                      {renderEditButton(EditableFields.classification)}
                    </Row>
                    {renderActionButtons(EditableFields.classification)}
                  </FieldWrapper>
                </StyledRow>
                <StyledRow
                  className={getRowClassName(EditableFields.licenseNumber)}
                >
                  <FieldWrapper
                    isEdit={fieldToEdit === EditableFields.licenseNumber}
                  >
                    <Row>
                      <Col>
                        <StyledRowTitle
                          isEdit={fieldToEdit === EditableFields.licenseNumber}
                        >
                          {t('common:labels.license_number')}
                        </StyledRowTitle>
                        <Controller
                          name="licenseNumber"
                          control={control}
                          render={({ field }) => (
                            <ClinTextInput
                              id="license-number"
                              {...field}
                              width="100%"
                              readOnly={
                                fieldToEdit !== EditableFields.licenseNumber
                              }
                              className={fieldToEdit == null ? 'inactive' : ''}
                              hasError={!!errors.licenseNumber}
                              prompt={errors.licenseNumber?.message}
                              value={
                                isDisabledLicenceNumber
                                  ? 'None'
                                  : field.value || ''
                              }
                              disabled={
                                isDisabledLicenceNumber &&
                                fieldToEdit === EditableFields.licenseNumber
                              }
                            />
                          )}
                        />
                      </Col>
                      {renderEditButton(EditableFields.licenseNumber)}
                    </Row>
                    {renderActionButtons(
                      EditableFields.licenseNumber,
                      isDisabledLicenceNumber
                    )}
                  </FieldWrapper>
                </StyledRow>
              </StyledAccountDetailsSummaryItem>

              <StyledAccountDetailsSummaryItem paddingLeft>
                <ClinText
                  fontWeight={ClinTheme.fontWeights.bold}
                  lineHeight="20px"
                  marginBottom={ClinTheme.space[3]}
                >
                  {t('account_details:institutes')}
                </ClinText>
                <StyledRow>
                  <Row>
                    <Col>
                      <StyledRowTitle>
                        <span
                          style={{
                            textTransform: 'capitalize'
                          }}
                        >
                          {t('institute_details:current')}
                        </span>
                      </StyledRowTitle>
                      <StyledRowText>{institute.instituteName}</StyledRowText>
                    </Col>
                  </Row>
                </StyledRow>
                <StyledRow>
                  <Row>
                    <Col>
                      <StyledRowTitle>
                        {t('common:labels.permissions')}
                      </StyledRowTitle>
                      <StyledRowText>
                        {role?.contact_type && role.contact_type}
                      </StyledRowText>
                    </Col>
                  </Row>
                </StyledRow>
                {userDetails.customerPhones.length !== 0 &&
                  watchCustomerPhones.length !== 0 &&
                  watchCustomerPhones.map((item: UserPhoneDto, index) => {
                    const responseType =
                      watch(`customerPhones.${index}.countryCode`) === '' &&
                      watch(`customerPhones.${index}.areaCode`) === '' &&
                      watch(`customerPhones.${index}.phoneNumber`) === ''
                        ? PhoneContactResponseType.DELETE
                        : PhoneContactResponseType.UPDATE

                    // create unique edit field per phone row
                    const currentField = `${getPhoneNumberField(item)}_${
                      item.phoneId
                    }`

                    return (
                      <StyledRow
                        className="phone-fields"
                        key={`phone-number-${item.phoneId}`}
                      >
                        <FieldWrapper isEdit={fieldToEdit === currentField}>
                          <Row>
                            <Col>
                              <Row>
                                <Col>
                                  {fieldToEdit !== currentField && (
                                    <StyledRowTitle>
                                      {t(`${getPhoneNumberKey(item)}`)}
                                    </StyledRowTitle>
                                  )}
                                </Col>
                              </Row>
                              <Row>
                                {fieldToEdit !== currentField && (
                                  <Col>
                                    {item.phoneNumber ? (
                                      <Row key={`phone-number-${item.phoneId}`}>
                                        <Col>
                                          <StyledRowText>
                                            {item.countryCode
                                              ? `+${item.countryCode} `
                                              : ''}
                                            {item.areaCode
                                              ? `${item.areaCode} `
                                              : ''}
                                            {item.phoneNumber}
                                          </StyledRowText>
                                          {item.phoneExtension && (
                                            <>
                                              <StyledPhoneExtension>
                                                Ext:
                                              </StyledPhoneExtension>
                                              <StyledPhoneExtensionNumber>
                                                {item.phoneExtension}
                                              </StyledPhoneExtensionNumber>
                                            </>
                                          )}
                                        </Col>
                                      </Row>
                                    ) : (
                                      <StyledRowText>--</StyledRowText>
                                    )}
                                  </Col>
                                )}
                                <Col
                                  xs={12}
                                  style={{
                                    display:
                                      fieldToEdit === currentField &&
                                      userDetails.customerPhones.length
                                        ? 'block'
                                        : 'none'
                                  }}
                                >
                                  <Row>
                                    <Col xs={12}>
                                      <div style={{ display: 'none' }}>
                                        <ClinTextInput
                                          id={`phone-id-${index}`}
                                          {...register(
                                            `customerPhones.${index}.phoneId`
                                          )}
                                        />
                                        <ClinTextInput
                                          id={`phone-type-${index}`}
                                          {...register(
                                            `customerPhones.${index}.phoneType`
                                          )}
                                          value={item.phoneType}
                                        />
                                        <ClinTextInput
                                          id={`request-type-${index}`}
                                          {...register(
                                            `customerPhones.${index}.requestType`
                                          )}
                                          value={responseType}
                                        />
                                      </div>
                                      <Controller
                                        name={`customerPhones.${index}.countryCode`}
                                        control={control}
                                        render={({ field: { onChange } }) => {
                                          return (
                                            <>
                                              <StyledRowTitle isEdit>
                                                {t(
                                                  'common:labels.country_code'
                                                )}
                                              </StyledRowTitle>
                                              <ClinSelect
                                                id={`country-code-${index}`}
                                                width="100%"
                                                defaultValue={item.countryCode}
                                                variant={
                                                  ClinSelectVariant.primaryV2
                                                }
                                                onChange={(
                                                  changeValue: ChangeEvent<HTMLSelectElement>
                                                ) =>
                                                  onChange(
                                                    changeValue.currentTarget
                                                      .value
                                                  )
                                                }
                                                hasError={
                                                  !!getPhoneError(
                                                    index,
                                                    'countryCode'
                                                  )
                                                }
                                                prompt={getPhoneError(
                                                  index,
                                                  'countryCode'
                                                )}
                                              >
                                                <option value="">
                                                  {t(
                                                    'common:labels.select_placeholder'
                                                  )}
                                                </option>
                                                <option value="44">+44</option>
                                                {countryCodes.map(
                                                  (
                                                    code: string,
                                                    index: number
                                                  ) => {
                                                    return (
                                                      <option
                                                        key={`code-${index}`}
                                                        value={code}
                                                      >
                                                        {`+${code}`}
                                                      </option>
                                                    )
                                                  }
                                                )}
                                              </ClinSelect>

                                              <ClinSpacer
                                                height={ClinTheme.space[3]}
                                              />
                                            </>
                                          )
                                        }}
                                      />
                                    </Col>
                                    <Col xs={12}>
                                      <StyledRowTitle isEdit>
                                        {t('common:labels.area_code')}
                                      </StyledRowTitle>
                                      <ClinTextInput
                                        id={`area-code-${index}`}
                                        {...register(
                                          `customerPhones.${index}.areaCode`
                                        )}
                                        type="tel"
                                        width="100%"
                                        readOnly={
                                          !(fieldToEdit === currentField)
                                        }
                                        hasError={
                                          !!errors.customerPhones?.[index]
                                            ?.areaCode
                                        }
                                        prompt={
                                          errors.customerPhones?.[index]
                                            ?.areaCode?.message
                                        }
                                        onChange={(e) => {
                                          handleAreaCodeChange(
                                            e.target.value,
                                            index
                                          )
                                        }}
                                        onFocus={(e) => {
                                          handleAreaCodeChange(
                                            e.target.value,
                                            index
                                          )
                                        }}
                                      />

                                      <ClinSpacer height={ClinTheme.space[3]} />
                                    </Col>
                                    <Col xs={12}>
                                      <StyledRowTitle isEdit>
                                        {t(
                                          `${
                                            phoneTypesRecord[
                                              item.phoneType as TPhoneResponseType
                                            ]?.translationKey
                                          }`
                                        )}
                                        {!hasAtLeastOnePhone && '*'}
                                      </StyledRowTitle>
                                      <ClinTextInput
                                        id={`phone-number-${index}`}
                                        {...register(
                                          `customerPhones.${index}.phoneNumber`
                                        )}
                                        width="100%"
                                        type="tel"
                                        readOnly={
                                          !(fieldToEdit === currentField)
                                        }
                                        hasError={
                                          !!errors.customerPhones?.[index]
                                            ?.phoneNumber ||
                                          (!!errors.customerPhones &&
                                            !Array.isArray(
                                              errors.customerPhones
                                            ))
                                        }
                                        prompt={
                                          errors.customerPhones?.[index]
                                            ?.phoneNumber?.message ||
                                          (!Array.isArray(errors.customerPhones)
                                            ? errors.customerPhones?.message
                                            : undefined)
                                        }
                                        onChange={(e) => {
                                          handlePhoneChange(
                                            e.target.value,
                                            index
                                          )
                                        }}
                                        onFocus={(e) => {
                                          handlePhoneChange(
                                            e.target.value,
                                            index
                                          )
                                        }}
                                      />

                                      {item.phoneType !==
                                        PhoneResponseType.MOBILE && (
                                        <ClinSpacer
                                          height={ClinTheme.space[3]}
                                        />
                                      )}
                                    </Col>
                                    <Col xs={12}>
                                      {item.phoneType !==
                                        PhoneResponseType.MOBILE && (
                                        <>
                                          <StyledRowTitle isEdit>
                                            {t('account_details:extension')}
                                          </StyledRowTitle>
                                          <ClinTextInput
                                            id={`extension-${index}`}
                                            {...register(
                                              `customerPhones.${index}.phoneExtension`
                                            )}
                                            type="tel"
                                            width="100%"
                                            readOnly={
                                              !(fieldToEdit === currentField)
                                            }
                                            hasError={
                                              !!errors.customerPhones?.[index]
                                                ?.phoneExtension
                                            }
                                            prompt={
                                              errors.customerPhones?.[index]
                                                ?.phoneExtension?.message
                                            }
                                            onChange={(e) => {
                                              handleExtensionChange(
                                                e.target.value,
                                                index
                                              )
                                            }}
                                            onFocus={(e) => {
                                              handleExtensionChange(
                                                e.target.value,
                                                index
                                              )
                                            }}
                                          />
                                        </>
                                      )}
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            </Col>
                            {renderEditButton(currentField)}
                          </Row>
                          {renderActionButtons(currentField)}
                        </FieldWrapper>
                      </StyledRow>
                    )
                  })}

                {userDetails.customerPhones.length === 0 && (
                  <StyledRow className="phone-fields">
                    <FieldWrapper
                      isEdit={fieldToEdit === EditableFields.phoneNumber}
                    >
                      <Row>
                        <Col>
                          <Row>
                            <Col>
                              {fieldToEdit !== EditableFields.phoneNumber && (
                                <StyledRowTitle>
                                  {t('common:labels.phone_number_one')}
                                </StyledRowTitle>
                              )}
                            </Col>
                          </Row>
                          <Row>
                            {fieldToEdit !== EditableFields.phoneNumber ? (
                              <Col xs={12}>
                                {watchCustomerPhones.length !== 0 &&
                                  watchCustomerPhones.map(
                                    (item: UserPhoneDto) =>
                                      item?.phoneNumber && (
                                        <Row
                                          key={`phone-number-${item?.phoneId}-${item?.phoneNumber}`}
                                        >
                                          <Col>
                                            <StyledRowText>
                                              {item.countryCode
                                                ? `+${item.countryCode} `
                                                : ''}
                                              {item.areaCode
                                                ? `${item.areaCode} `
                                                : ''}
                                              {item.phoneNumber}
                                            </StyledRowText>
                                            {item.phoneExtension && (
                                              <>
                                                <StyledPhoneExtension>
                                                  Ext:
                                                </StyledPhoneExtension>
                                                <StyledPhoneExtensionNumber>
                                                  {item.phoneExtension}
                                                </StyledPhoneExtensionNumber>
                                              </>
                                            )}
                                          </Col>
                                        </Row>
                                      )
                                  )}
                                {watchCustomerPhones.length === 0 && (
                                  <StyledRowText>--</StyledRowText>
                                )}
                              </Col>
                            ) : (
                              <Col xs={12}>
                                {userDetails.customerPhones.length === 0 && (
                                  <Row key={`phone-type-0`}>
                                    <Col xs={12}>
                                      <div style={{ display: 'none' }}>
                                        <ClinTextInput
                                          id="phone-type"
                                          {...register(
                                            `customerPhones.0.phoneType`
                                          )}
                                          value={phoneTypesRecord['GEN'].type}
                                        />
                                        <ClinTextInput
                                          id="request-type"
                                          {...register(
                                            `customerPhones.0.requestType`
                                          )}
                                          value={
                                            PhoneContactResponseType.CREATE
                                          }
                                        />
                                      </div>
                                      <Controller
                                        name={`customerPhones.0.countryCode`}
                                        control={control}
                                        defaultValue={'44'}
                                        render={({
                                          field: { onChange, value }
                                        }) => {
                                          !value &&
                                            setValue(
                                              'customerPhones.0.countryCode',
                                              '44',
                                              {
                                                shouldDirty: true
                                              }
                                            )
                                          return (
                                            <>
                                              <StyledRowTitle isEdit>
                                                {t(
                                                  'common:labels.country_code'
                                                )}
                                              </StyledRowTitle>
                                              <ClinSelect
                                                id="country-code"
                                                width="100%"
                                                defaultValue={value}
                                                variant={
                                                  ClinSelectVariant.primaryV2
                                                }
                                                onChange={(
                                                  changeValue: ChangeEvent<HTMLSelectElement>
                                                ) =>
                                                  onChange(
                                                    changeValue.currentTarget
                                                      .value
                                                  )
                                                }
                                                hasError={
                                                  !!getPhoneError(
                                                    0,
                                                    'countryCode'
                                                  )
                                                }
                                                prompt={getPhoneError(
                                                  0,
                                                  'countryCode'
                                                )}
                                              >
                                                <option value="">
                                                  {t(
                                                    'common:labels.select_placeholder'
                                                  )}
                                                </option>
                                                <option value="44">+44</option>
                                                {countryCodes.map(
                                                  (
                                                    code: string,
                                                    index: number
                                                  ) => {
                                                    return (
                                                      <option
                                                        key={`code-${index}`}
                                                        value={code}
                                                      >
                                                        {`+${code}`}
                                                      </option>
                                                    )
                                                  }
                                                )}
                                              </ClinSelect>

                                              <ClinSpacer
                                                height={ClinTheme.space[3]}
                                              />
                                            </>
                                          )
                                        }}
                                      />
                                    </Col>
                                    <Col xs={12}>
                                      <StyledRowTitle isEdit>
                                        {t('common:labels.area_code')}
                                      </StyledRowTitle>
                                      <ClinTextInput
                                        id="area-code"
                                        {...register(
                                          `customerPhones.0.areaCode`
                                        )}
                                        type="tel"
                                        width="100%"
                                        hasError={
                                          !!errors.customerPhones?.[0]?.areaCode
                                        }
                                        prompt={
                                          errors.customerPhones?.[0]?.areaCode
                                            ?.message
                                        }
                                        readOnly={
                                          !(
                                            fieldToEdit ===
                                            EditableFields.phoneNumber
                                          )
                                        }
                                        onChange={(e) => {
                                          handleAreaCodeChange(
                                            e.target.value,
                                            0
                                          )
                                        }}
                                        onFocus={(e) => {
                                          handleAreaCodeChange(
                                            e.target.value,
                                            0
                                          )
                                        }}
                                      />

                                      <ClinSpacer height={ClinTheme.space[3]} />
                                    </Col>
                                    <Col xs={12}>
                                      <StyledRowTitle isEdit>
                                        {t('common:labels.phone')}
                                        {!hasAtLeastOnePhone && '*'}
                                      </StyledRowTitle>
                                      <ClinTextInput
                                        id="phone-number"
                                        {...register(
                                          `customerPhones.0.phoneNumber`
                                        )}
                                        width="100%"
                                        type="tel"
                                        readOnly={
                                          !(
                                            fieldToEdit ===
                                            EditableFields.phoneNumber
                                          )
                                        }
                                        hasError={
                                          !!errors.customerPhones?.[0]
                                            ?.phoneNumber ||
                                          (!!errors.customerPhones &&
                                            !Array.isArray(
                                              errors.customerPhones
                                            ))
                                        }
                                        prompt={
                                          errors.customerPhones?.[0]
                                            ?.phoneNumber?.message ||
                                          (!Array.isArray(errors.customerPhones)
                                            ? errors.customerPhones?.message
                                            : undefined)
                                        }
                                        onChange={(e) => {
                                          handlePhoneChange(e.target.value, 0)
                                        }}
                                        onFocus={(e) => {
                                          handlePhoneChange(e.target.value, 0)
                                        }}
                                      />

                                      <ClinSpacer height={ClinTheme.space[3]} />
                                    </Col>
                                    <Col xs={12}>
                                      <StyledRowTitle isEdit>
                                        {t('account_details:extension')}
                                      </StyledRowTitle>
                                      <ClinTextInput
                                        id="extension"
                                        {...register(
                                          `customerPhones.0.phoneExtension`
                                        )}
                                        type="tel"
                                        width="100%"
                                        readOnly={
                                          !(
                                            fieldToEdit ===
                                            EditableFields.phoneNumber
                                          )
                                        }
                                        hasError={
                                          !!errors.customerPhones?.[0]
                                            ?.phoneExtension
                                        }
                                        prompt={
                                          errors.customerPhones?.[0]
                                            ?.phoneExtension?.message
                                        }
                                        onChange={(e) => {
                                          handleExtensionChange(
                                            e.target.value,
                                            0
                                          )
                                        }}
                                        onFocus={(e) => {
                                          handleExtensionChange(
                                            e.target.value,
                                            0
                                          )
                                        }}
                                      />
                                    </Col>
                                  </Row>
                                )}
                              </Col>
                            )}
                          </Row>
                        </Col>
                        {renderEditButton(EditableFields.phoneNumber)}
                      </Row>
                      {renderActionButtons(EditableFields.phoneNumber)}
                    </FieldWrapper>
                  </StyledRow>
                )}
              </StyledAccountDetailsSummaryItem>
            </StyledAccountDetailsSummaryContainer>
          </StyledAccountDetailsSummary>
        </form>

        <ClinSpacer height={ClinTheme.space[5]} />

        <StyledAccountDetailsBar>
          <div>
            <ClinText color={ClinTheme.colors.darkGrey}>
              {t('institute_details:account_number')} {institute.instituteId}
            </ClinText>
            <ClinText
              fontWeight={ClinTheme.fontWeights.bold}
              lineHeight="20px"
              marginTop={ClinTheme.space[1]}
            >
              {institute.instituteName}
            </ClinText>
          </div>
          <div>
            <ClinGroup
              alignItems="center"
              justifyContent="flex-end"
              direction={isMobile ? 'column' : 'row'}
            >
              {isAdmin && (
                <ClinButton
                  variant="primary"
                  fontSize={ClinTheme.fontSizes[2]}
                  onClick={() => onRequestNewUser()}
                  style={isMobile ? { width: '100%' } : {}}
                >
                  {t('institute_details:invite_new_user_btn')}
                </ClinButton>
              )}
              <ClinButton
                onClick={() => handleChangeInstitute()}
                variant="linkButton"
                fontSize={ClinTheme.fontSizes[2]}
                style={{ marginBottom: ClinTheme.space[0] }}
              >
                <ClinIcon
                  viewBox="0 0 24 24"
                  style={{ marginRight: '8px' }}
                  iconName={ClinIconPathName.Switch}
                />
                {t('institute_details:change_institute')}
              </ClinButton>
            </ClinGroup>
          </div>
        </StyledAccountDetailsBar>

        <ClinSpacer height={ClinTheme.space[3]} />

        <StyledUsersShippingContainer>
          {isAdmin && (
            <StyledUsers>
              {isLoadingContacts ? (
                <StyledSpinnerContainer>
                  <ClinSpinner size={ClinTheme.space[7]} />
                </StyledSpinnerContainer>
              ) : (
                <>
                  {!!pendingContacts?.length && (
                    <>
                      <StyledUsersBar>
                        <ClinText
                          fontWeight={ClinTheme.fontWeights.bold}
                          lineHeight="20px"
                        >
                          {t('institute_details:pending_invites')}
                        </ClinText>
                        <StyledCountBadge>
                          {pendingContacts?.length}
                        </StyledCountBadge>
                      </StyledUsersBar>
                      <ClinSpacer height={ClinTheme.space[2]} />
                      {pendingContacts?.map((contact, index) => (
                        <AccountDetailsUserCard
                          key={`contact-${contact.contactId}-${index}`}
                          userKey={`${contact.contactId}-${index}`}
                          isUser={userDetails.email === contact.contactEmail}
                          isPending={true}
                          contact={contact}
                          editingKey={editingKey}
                          isUserSubmitting={isUserSubmitting}
                          specialisms={specialisms}
                          jobTitles={jobTitles}
                          isSpecialismsLoading={isSpecialismsLoading}
                          isJobTitlesLoading={isJobTitlesLoading}
                          onEditUser={onEditUser}
                          onRemoveUser={onRemoveUser}
                          onRequestInvite={onRequestInvite}
                          handleUserFormSubmission={handleUserFormSubmission}
                          handleUserFormCancel={handleUserFormCancel}
                        />
                      ))}
                    </>
                  )}
                  <StyledUsersBar>
                    <ClinText
                      fontWeight={ClinTheme.fontWeights.bold}
                      lineHeight="20px"
                    >
                      {t('institute_details:active_users')}
                    </ClinText>
                    <StyledCountBadge>
                      {activeContacts?.length}
                    </StyledCountBadge>
                  </StyledUsersBar>
                  <ClinSpacer height={ClinTheme.space[2]} />
                  {activeContacts?.map((contact, index) => (
                    <AccountDetailsUserCard
                      key={`contact-${contact.contactId}-${index}`}
                      userKey={`${contact.contactId}-${index}`}
                      isUser={userDetails.email === contact.contactEmail}
                      isPending={false}
                      contact={contact}
                      editingKey={editingKey}
                      isUserSubmitting={isUserSubmitting}
                      specialisms={specialisms}
                      jobTitles={jobTitles}
                      isSpecialismsLoading={isSpecialismsLoading}
                      isJobTitlesLoading={isJobTitlesLoading}
                      onEditUser={onEditUser}
                      onRemoveUser={onRemoveUser}
                      handleUserFormSubmission={handleUserFormSubmission}
                      handleUserFormCancel={handleUserFormCancel}
                    />
                  ))}
                </>
              )}
            </StyledUsers>
          )}

          <StyledShippingAddress>
            <StyledShippingAddressBar>
              <ClinText
                fontWeight={ClinTheme.fontWeights.bold}
                lineHeight="20px"
              >
                {t('institute_details:shipping_addresses')}
              </ClinText>
              <StyledClinSecondaryButton2
                style={{
                  width: isMobile ? '100%' : 'auto'
                }}
                variant="secondary"
                disabled={isRequestNewAddress}
                onClick={() => onRequestNewShippingAddress()}
              >
                {t('institute_details:request_new_shipping_address')}
              </StyledClinSecondaryButton2>
            </StyledShippingAddressBar>

            <ClinSpacer height={ClinTheme.space[2]} />

            {isRequestNewAddress && (
              <>
                <StyledAccountDetailsAddress>
                  <AccountDetailsAddressForm
                    isSubmitting={isSubmittingAddress}
                    countries={countries}
                    handleFormSubmit={handleAddressSubmission}
                    resetOnSubmit={resetOnSubmit}
                    addressErrorMessage={addressErrorMessage}
                    selectedCountry={selectedCountry}
                    supportContact={supportContact}
                    setIsRequestNewAddress={setIsRequestNewAddress}
                  />
                </StyledAccountDetailsAddress>
                <ClinSpacer height={ClinTheme.space[2]} />
              </>
            )}

            <StyledShippingAddressItem>
              <ClinText
                fontWeight={ClinTheme.fontWeights.bold}
                lineHeight="20px"
                marginBottom={ClinTheme.space[3]}
              >
                {t('institute_details:primary_address')}
                <StyledPrimaryBadge>
                  {t('account_details:selected')}
                </StyledPrimaryBadge>
              </ClinText>

              {addressesShipTo.map((address: OrgAddressDto) =>
                address.primaryFlag === 'Y' ? (
                  <AccountDetailsAddressBlock
                    key={address.addressId}
                    address={address}
                    isAdmin={isAdmin}
                    onRemove={onRemoveAddress}
                  />
                ) : null
              )}

              <ClinSpacer height={ClinTheme.space[2]} />
              <ClinSpacer height={ClinTheme.space[1]} />

              <ClinAnnounceBar
                mode={AnnounceMode.Information}
                title={t('institute_details:shipping_addresses')}
              >
                <Trans
                  i18nKey="institute_details:request_to_add_or_remove_address_contact"
                  components={{
                    phone: (
                      <a
                        aria-label="Phone number link to customer support"
                        href={`tel:${supportContact.rawPhoneNumber}`}
                        style={{ textDecoration: 'underline' }}
                      />
                    ),
                    email: (
                      <a
                        aria-label="Mail to customer support"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`mailto:${supportContact.csEmailAddress}`}
                        style={{ textDecoration: 'underline' }}
                        onClick={() => {
                          analyticsServiceSingleton.trackEvent(
                            AnalyticsEvent.ContactClicked,
                            { contactType: 'email' }
                          )
                        }}
                      />
                    )
                  }}
                  values={{
                    phoneNumber: supportContact.csPhoneNumber,
                    emailAddress: supportContact.csEmailAddress
                  }}
                >
                  <></>
                </Trans>
              </ClinAnnounceBar>
            </StyledShippingAddressItem>

            <ClinSpacer height={ClinTheme.space[2]} />
            <StyledShippingAddressSubBar>
              <ClinGroup space={ClinTheme.space[1]} alignItems={'center'}>
                <ClinText
                  fontWeight={ClinTheme.fontWeights.bold}
                  lineHeight="20px"
                  marginBottom={ClinTheme.space[3]}
                >
                  {t('account_details:additional')}
                </ClinText>
                <StyledCountBadge>
                  {additionalAddresses.length}
                </StyledCountBadge>
              </ClinGroup>
            </StyledShippingAddressSubBar>

            {additionalAddresses.map(
              (address: OrgAddressDto, index: number) => (
                <Fragment key={`additional-${index}`}>
                  <ClinSpacer height={ClinTheme.space[2]} />
                  <StyledShippingAddressItem
                    key={address.addressId}
                    borderRadius={`${ClinTheme.space[1]}px`}
                  >
                    <AccountDetailsAddressBlock
                      address={address}
                      isAdmin={isAdmin}
                      onRemove={onRemoveAddress}
                    />
                  </StyledShippingAddressItem>
                </Fragment>
              )
            )}

            <ClinSpacer height={ClinTheme.space[4]} />

            <StyledShippingAddressSubBar>
              <ClinText
                fontWeight={ClinTheme.fontWeights.bold}
                lineHeight="20px"
              >
                {t('institute_details:billing_information')}
              </ClinText>
            </StyledShippingAddressSubBar>

            <ClinSpacer height={ClinTheme.space[2]} />

            {institute.billTo && (
              <StyledShippingAddressItem>
                <ClinText
                  color={ClinTheme.colors.darkGrey}
                  fontWeight={ClinTheme.fontWeights.medium}
                >
                  {t('account_details:address')}
                </ClinText>
                <ClinText fontWeight={ClinTheme.fontWeights.medium}>
                  {[
                    institute?.billTo.address1,
                    institute?.billTo.address2,
                    institute?.billTo.address3,
                    institute?.billTo.address4,
                    institute?.billTo.state,
                    institute?.billTo.country,
                    institute?.billTo.postalCode
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </ClinText>

                <ClinSpacer height={ClinTheme.space[2]} />
                <ClinSpacer height={ClinTheme.space[1]} />

                <ClinAnnounceBar
                  mode={AnnounceMode.Information}
                  title={t(
                    'institute_details:billing_information_announce_title'
                  )}
                >
                  <Trans
                    i18nKey="institute_details:billing_information_announce_contact"
                    components={{
                      email: (
                        <a
                          aria-label="Mail to creditcontrol@clinigengroup.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          href="mailto:creditcontrol@clinigengroup.com"
                          style={{ textDecoration: 'underline' }}
                        />
                      )
                    }}
                  >
                    <></>
                  </Trans>
                </ClinAnnounceBar>
              </StyledShippingAddressItem>
            )}
          </StyledShippingAddress>
        </StyledUsersShippingContainer>
      </ClinPageContentFrame>
    </ClinPageContentFrame>
  )
}
